import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, darken, lighten, Typography, useTheme } from '@mui/material';
import { DeviceStateIndicator } from 'console/shared/components/indicators/device-state';
import { memo, useCallback, useEffect, useMemo, useState, } from 'react';
import { AccordionWithValidation } from 'z3k-settings/components/material-utils/accordion-with-validation';
import alarm_audio from 'console/audio/zth-dashboard-alert.mp3';
import { useMounted } from 'shared/hooks/utils';
import { Visibility } from '@mui/icons-material';
import { DeviceIconState } from 'console/device/icon/types';
import { alarm_device_icon_states, translate_device_icon_state, } from 'console/device/icon/transform';
import { useZ3KDashboardDevices } from 'console/shared/hooks/device/z3k';
import { useDashboard } from '../../provider';
import { useBooleanState } from 'shared/hooks/state/boolean';
const alarm_button_sx = ({ palette }, is_warning) => ({
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    border: `1px solid ${palette.error.contrastText}`,
    borderRadius: 1.5,
    transition: 'all 0.2s linear',
    color: palette.error.contrastText,
    '&:hover': {
        backgroundColor: darken(is_warning ? palette.warning.main : palette.error.main, 0.1),
    },
    p: 1,
    overflow: 'hidden',
    gap: 1.5,
    fontSize: '1.5rem',
});
const viewed_button_sx = (theme, is_warning) => ({
    ...alarm_button_sx(theme, is_warning),
    fontSize: '0.9rem',
    p: 0.5,
    gap: 1,
});
const header_props = (theme, is_warning, show_animation) => {
    const { palette } = theme;
    const bgcolor = is_warning ? palette.warning.main : palette.error.main;
    return {
        sx: {
            '@keyframes pulse': {
                '20%': {
                    bgcolor,
                },
                '40%': {
                    bgcolor: lighten(bgcolor, 0.3),
                },
                '60%': {
                    bgcolor,
                },
                '80%': {
                    bgcolor: lighten(bgcolor, 0.3),
                },
            },
            color: `error.contrastText`,
            bgcolor,
            animation: show_animation ? `1.5s infinite alternate pulse` : undefined,
            '& .MuiSvgIcon-root': {
                color: `error.contrastText`,
            },
        },
    };
};
const dedails_props = (is_warning) => ({
    sx: {
        color: `error.contrastText`,
        bgcolor: `${is_warning ? 'warning' : 'error'}.main`,
        '& .MuiSvgIcon-root': {
            color: `error.contrastText`,
        },
    },
});
const sx = {
    borderBottom: `1px solid divider`,
    textAlign: ' center',
    '&.MuiAccordion-root.Mui-expanded': {
        m: 0,
    },
};
const warning_device_states = [
    DeviceIconState.guard_zone_alarm,
    DeviceIconState.sensor_alarm,
];
const alarm_sound = new Audio(alarm_audio);
export const AlarmDevices = memo(function AlarmDevices() {
    const { devices_ids, devices_rfs } = useDashboard();
    const is_mounted = useMounted();
    const devices = useZ3KDashboardDevices(devices_ids);
    const theme = useTheme();
    const initial_alarm_devices = useMemo(() => {
        const alarm_devices = devices.filter(({ iconState }) => alarm_device_icon_states.includes(iconState));
        return alarm_devices.map(({ id, iconState, name, individual_device_name }) => ({
            id,
            icon_state: iconState,
            name: individual_device_name || name,
            ref: devices_rfs[id],
        }));
    }, [devices]);
    const [alarm_devices, setAlarmDevices] = useState(initial_alarm_devices);
    useEffect(() => {
        if (is_mounted.current) {
            setAlarmDevices(initial_alarm_devices);
        }
    }, [devices_ids]);
    const onAlarmDeviceClick = useCallback((ref) => () => {
        if (ref && ref.current) {
            ref.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, []);
    const is_only_warning_devices = useMemo(() => alarm_devices.every((device) => warning_device_states.includes(device.icon_state)), [alarm_devices]);
    const alarm_devices_view = useMemo(() => {
        if (alarm_devices.length === 0) {
            return null;
        }
        return alarm_devices.map(({ id, name, icon_state, ref }) => (_jsxs(Button, { sx: (theme) => alarm_button_sx(theme, is_only_warning_devices), onClick: onAlarmDeviceClick(ref), children: [_jsx(DeviceStateIndicator, { state: icon_state }), _jsxs(Box, { display: "flex", width: "100%", flexDirection: "column", gap: 0.2, overflow: "hidden", children: [_jsx(Typography, { fontSize: "0.85rem", textAlign: "center", textOverflow: "ellipsis", children: name }), _jsx(Typography, { fontSize: "0.85rem", textAlign: "center", children: translate_device_icon_state[icon_state] })] })] }, id)));
    }, [alarm_devices, is_only_warning_devices]);
    const [alarm_audio_stopped, , stopAlarmAudio] = useBooleanState(false);
    useEffect(() => {
        if (is_mounted.current) {
            if (alarm_devices_view) {
                alarm_sound.play();
            }
            alarm_sound.loop = !alarm_audio_stopped && Boolean(alarm_devices_view);
        }
        return () => {
            alarm_sound.pause();
        };
    }, [alarm_audio_stopped, alarm_devices_view]);
    return (alarm_devices_view && (_jsx(AccordionWithValidation, { validation_result: null, headerProps: header_props(theme, is_only_warning_devices, !alarm_audio_stopped), detailsProps: dedails_props(is_only_warning_devices), sx: sx, validation_warnings_result: null, header: _jsx(Box, { width: "100%", children: _jsx(Typography, { textAlign: "center", children: is_only_warning_devices ? 'Сработка датчика' : 'Авария' }) }), children: _jsxs(Box, { display: "flex", flexDirection: "column", rowGap: 1, px: 1, py: 0.5, children: [_jsx(Box, { gap: 1, flexWrap: "wrap", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", children: alarm_devices_view }), !alarm_audio_stopped && (_jsx(Button, { sx: (theme) => viewed_button_sx(theme, is_only_warning_devices), onClick: stopAlarmAudio, endIcon: _jsx(Visibility, {}), children: "\u041E\u043F\u043E\u0432\u0435\u0449\u0435\u043D\u0438\u0435 \u043F\u0440\u043E\u0441\u043C\u043E\u0442\u0440\u0435\u043D\u043D\u043E" }))] }) })));
});
